import React, {
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
} from "react";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../api";
import { downloadBlob } from "../utils/downloadBlob";

const ExportJobsContext = createContext();

const isMissingJobError = (error) => {
  const collectMessages = [];

  if (error?.message) {
    collectMessages.push(error.message);
  }

  if (Array.isArray(error?.graphQLErrors)) {
    collectMessages.push(
      ...error.graphQLErrors.map((graphQLError) => graphQLError.message),
    );
  }

  if (Array.isArray(error?.networkError?.result?.errors)) {
    collectMessages.push(
      ...error.networkError.result.errors.map(
        (networkError) => networkError.message,
      ),
    );
  }

  return collectMessages.some((message = "") =>
    message.toLowerCase().includes("job matching query does not exist"),
  );
};

export const ExportJobsProvider = ({ children }) => {
  const ApiClient = useApiClient();

  const [activeJobs, setActiveJobs] = useState(() => {
    const stored = localStorage.getItem("exportJobs");
    return stored ? JSON.parse(stored) : [];
  });

  // persist in localStorage
  useEffect(() => {
    localStorage.setItem("exportJobs", JSON.stringify(activeJobs));
  }, [activeJobs]);

  const addJob = useCallback((job, metadata = {}) => {
    setActiveJobs((prev) => [...prev, { id: job.id, ...metadata }]);
    const message =
      metadata.message ||
      "Feedback requested, your download will start when ready.";
    enqueueSnackbar(message, {
      variant: "success",
      autoHideDuration: 3000,
    });
  }, []);

  // Poll jobs
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!activeJobs.length) return;

      for (const job of activeJobs) {
        try {
          const res = await ApiClient.core.getJobStatus({ jobId: job.id });
          if (res.status.toLowerCase() === "completed") {
            enqueueSnackbar(`Export ready for download`, {
              variant: "success",
            });
            setActiveJobs((prev) => prev.filter((j) => j.id !== job.id));
            await downloadBlob(res.signedUrl, job.filename); // currently setting pre signed URL as message
          } else if (res.status.toLowerCase() === "failed") {
            enqueueSnackbar(`Export failed`, { variant: "error" });
            setActiveJobs((prev) => prev.filter((j) => j.id !== job.id));
          }
        } catch (e) {
          console.error("Polling failed", e);

          if (isMissingJobError(e)) {
            enqueueSnackbar(
              "Export job expired. Please request a new export.",
              {
                variant: "warning",
              },
            );
            setActiveJobs((prev) => prev.filter((j) => j.id !== job.id));
          }
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeJobs, ApiClient]);

  return (
    <ExportJobsContext.Provider value={{ activeJobs, addJob }}>
      {children}
    </ExportJobsContext.Provider>
  );
};

export const useExportJobs = () => useContext(ExportJobsContext);
